import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import baseUrl from "../../../../../api/baseUrl";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  isTreatmentItemDeletable,
  isTreatmentItemEditable,
  isTreatmentItemPaid,
} from "./treatmentItemUtils";
import { toLocalYmd } from "../../../../../utils/colomboDate";

const user: string | undefined = getDecryptedToken("userName");

function AllSubTreatmentTr({
  items,
  refresh,
  _id,
  setallTeatment,
  item,
  nested = false,
  onEdit,
}: any) {
  const dateNow = new Date();
  const { ID } = useParams();
  const [status, setstatus] = useState(items?.status);
  const [planStatus, setPlanStatus] = useState(items?.planStatus || "planned");
  const isLongTerm = status === "long term";
  const isSuperAdmin = getDecryptedToken("role") === "Super Admin";
  const canEdit = isTreatmentItemEditable(items, isSuperAdmin);
  const canDelete = isTreatmentItemDeletable(items);

  useEffect(() => {
    setstatus(items?.status);
  }, [items?.status]);

  useEffect(() => {
    setPlanStatus(items?.planStatus || "planned");
  }, [items?.planStatus]);

  const handleDelete = (e: any) => {
    setallTeatment((prevTreatments: any) =>
      prevTreatments.map((row: any) => {
        if (row._id === _id) {
          return {
            ...row,
            subTreatment: row.subTreatment.filter(
              (subItem: any) => subItem._id !== e
            ),
          };
        }
        return row;
      })
    );
    baseUrl
      .patch("/client/treatments/subTreatment/delete-subtreetment", {
        maintreatmentid: ID,
        treatmentid: _id,
        subtreatmentid: e,
      })
      .then(() => {
        refresh();
      })
      .catch((error) => {
        console.error("Error removing treatment:", error);
      });
  };
  const handleSelectClick = () => {
    if (items?._id === undefined) {
      refresh();
    }
  };
  const handleSelect = (e: any) => {
    if (items?.status === "long term") return;
    if (items?._id) {
      setstatus(e.target.value);
      baseUrl
        .patch(
          "/client/treatments/subTreatment/add-subtreetmentStatus",
          { status: e.target.value },
          {
            headers: {
              maintreatmentid: ID,
              treatmentid: item._id,
              subtreatmentid: items._id,
            },
          }
        )
        .then(() => {
          refresh();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handlePlanStatusSelect = (e: any) => {
    if (!items?._id) return;
    const next = e.target.value;
    setPlanStatus(next);
    baseUrl
      .patch(
        "/client/treatments/subTreatment/add-subtreetmentStatus",
        { planStatus: next },
        {
          headers: {
            maintreatmentid: ID,
            treatmentid: item._id,
            subtreatmentid: items._id,
          },
        }
      )
      .then(() => {
        refresh();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <tr
      className={
        nested
          ? "hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
          : "hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors bg-slate-50/40 dark:bg-slate-700/20"
      }
    >
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-600 dark:text-slate-400"
            : "px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
        }
      >
        {toLocalYmd(items?.createdAt) || toLocalYmd(dateNow) || "—"}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200"
            : "px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200 pl-8 border-l-2 border-slate-200 dark:border-slate-600"
        }
      >
        {items?.treatment}
        {items?.type ? " - " + items?.type : ""}
        {items?.subtype ? " - " + items?.subtype : ""}
        {items?.stage ? " - " + items?.stage : ""}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-600 dark:text-slate-400"
            : "px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
        }
      >
        {items?.toothNos || items?.toothNo}
      </td>
      {!nested && (
        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
          {items?.surface}
        </td>
      )}
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-600 dark:text-slate-400"
            : "px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
        }
      >
        {items?.quantity}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-200"
            : "px-4 py-3 text-sm font-medium text-slate-800 dark:text-slate-200"
        }
      >
        {items?.price != null && items?.price !== ""
          ? isLongTerm
            ? `Est. Rs ${items.price}`
            : `Rs ${items.price}`
          : "-"}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-600 dark:text-slate-400"
            : "px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
        }
        onClick={handleSelectClick}
      >
        {isLongTerm ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-violet-200 dark:bg-violet-800/50 text-violet-800 dark:text-violet-200 cursor-default">
              Treatment Plan
            </span>
            <select
              value={planStatus}
              onChange={handlePlanStatusSelect}
              className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${
                planStatus === "Complete"
                  ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                  : planStatus === "ongoing"
                  ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                  : "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300"
              }`}
            >
              <option value="planned">Plan</option>
              <option value="ongoing">Ongoing</option>
              <option value="Complete">Complete</option>
            </select>
          </div>
        ) : (
          <select
            value={status ?? ""}
            onChange={handleSelect}
            className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${
              status === "Complete"
                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                : status === "ongoing"
                ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                : "bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300"
            }`}
          >
            {!items?.paid && <option value="planned">Planned</option>}
            <option value="ongoing">Ongoing</option>
            <option value="Complete">Complete</option>
          </select>
        )}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-600 dark:text-slate-400"
            : "px-4 py-3 text-sm text-slate-600 dark:text-slate-400"
        }
      >
        {items?.didBy
          ? items?.didBy?.firstName + " " + (items?.didBy?.lastName ?? "")
          : user}
      </td>
      <td
        className={
          nested
            ? "px-3 py-2 text-xs text-slate-500 dark:text-slate-400 text-left align-top max-w-[150px]"
            : "px-4 py-3 text-sm text-slate-500 dark:text-slate-400 text-left align-top max-w-[150px]"
        }
      >
        {wrapText(items?.note || "").map((line: string, idx: number) => (
          <div key={idx}>{line}</div>
        ))}
      </td>
      <td className={nested ? "px-3 py-2" : "px-4 py-3"}>
        <div className="flex items-center gap-1">
          {canEdit && onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 flex items-center justify-center transition-colors"
              title={
                isTreatmentItemPaid(items)
                  ? "Edit paid case treatment (Super Admin)"
                  : "Edit case treatment"
              }
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => handleDelete(items?._id)}
              className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex items-center justify-center transition-colors"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default AllSubTreatmentTr;
const wrapText = (text: string, maxLength: number = 35): string[] => {
  if (!text) return ["-"];
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    if (currentLine.length + word.length + 1 <= maxLength) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : ["-"];
};
